import {
  FilterOperator,
  FilterScope,
  getRelatedRecordSchemas,
  getSourceObjectSchema,
  isSelectableSourceObject,
  negateOperator,
  SourceObjectId,
} from './querySchema'

export type MetricCategory =
  | 'Core Production'
  | 'Speed & Quality Bonuses'
  | 'Response Time'
  | 'Dial Activity'
  | 'Loan Partner Requirements'
  | 'Quality Control & Penalties'

export interface MetricFilterRule {
  id: string
  scope: FilterScope
  relatedRecord?: string
  field: string
  operator: FilterOperator
  value: string
}

export type MetricExclusionRule = MetricFilterRule

export interface PointMetricDefinition {
  id: string
  category: MetricCategory
  label: string
  criteria: string
  points: number
  enabled: boolean
  sourceObject: SourceObjectId
  sourceUserField: string
  timeframeField: string
  conditions: MetricFilterRule[]
  customQueryEnabled: boolean
  customQueryText: string
}

type LegacyMetricDefinition = Omit<
  PointMetricDefinition,
  | 'sourceObject'
  | 'sourceUserField'
  | 'timeframeField'
  | 'conditions'
  | 'customQueryEnabled'
  | 'customQueryText'
> & {
  sourceObject?: SourceObjectId
  sourceUserField?: string
  timeframeField?: string
  conditions?: MetricFilterRule[]
  inclusions?: MetricFilterRule[]
  exclusions?: MetricFilterRule[] | string[]
  customQueryEnabled?: boolean
  customQueryText?: string
}

export const POINT_SYSTEM_CONFIG_STORAGE_KEY = 'mortgage-depot-point-system-config'

const RAW_POINT_METRICS: LegacyMetricDefinition[] = [
  {
    id: 'credit-pull',
    category: 'Core Production',
    label: 'Credit Pull',
    criteria: 'Credit report is pulled and recorded on an active loan file.',
    points: 750,
    enabled: true,
    exclusions: ['Do not count duplicate pulls on the same borrower within 30 days.'],
  },
  {
    id: 'doc-out',
    category: 'Core Production',
    label: 'Doc Out Sent',
    criteria: 'Initial disclosures are sent to the borrower.',
    points: 500,
    enabled: true,
    exclusions: ['Exclude disclosures sent only to correct a clerical error.'],
  },
  {
    id: 'submission',
    category: 'Core Production',
    label: 'Submission',
    criteria: 'A complete loan package is submitted to an eligible lender.',
    points: 500,
    enabled: true,
    exclusions: ['Exclude incomplete submissions returned before lender review.'],
  },
  {
    id: 'closing',
    category: 'Core Production',
    label: 'Closing',
    criteria: 'Loan closes and funds successfully.',
    points: 250,
    enabled: true,
    exclusions: ['Exclude rescinded or unfunded closings.'],
  },
  {
    id: 'speed-bonus',
    category: 'Speed & Quality Bonuses',
    label: 'Speed Bonus',
    criteria: 'Loan closes within 14 days of Credit Pull.',
    points: 1500,
    enabled: true,
    exclusions: ['Exclude files delayed by documented borrower-requested pauses.'],
  },
  {
    id: 'doc-out-signing',
    category: 'Speed & Quality Bonuses',
    label: 'Doc Out Signing Bonus',
    criteria: 'Borrower signs within 3 hours of disclosures being sent.',
    points: 250,
    enabled: true,
    exclusions: ['Exclude disclosures sent outside the borrower’s confirmed availability window.'],
  },
  {
    id: 'uwm-low-conditions',
    category: 'Speed & Quality Bonuses',
    label: 'UWM · 3 or fewer conditions',
    criteria: 'UWM submission receives three or fewer initial conditions.',
    points: 500,
    enabled: true,
    exclusions: [],
  },
  {
    id: 'uwm-high-conditions',
    category: 'Speed & Quality Bonuses',
    label: 'UWM · 10 or more conditions',
    criteria: 'UWM submission receives ten or more initial conditions.',
    points: -500,
    enabled: true,
    exclusions: ['Exclude conditions caused solely by lender system outages.'],
  },
  {
    id: 'other-low-conditions',
    category: 'Speed & Quality Bonuses',
    label: 'Other lenders · 5 or fewer conditions',
    criteria: 'Non-UWM submission receives five or fewer initial conditions.',
    points: 500,
    enabled: true,
    exclusions: [],
  },
  {
    id: 'other-high-conditions',
    category: 'Speed & Quality Bonuses',
    label: 'Other lenders · 12 or more conditions',
    criteria: 'Non-UWM submission receives twelve or more initial conditions.',
    points: -500,
    enabled: true,
    exclusions: [],
  },
  {
    id: 'answer-rate-95',
    category: 'Response Time',
    label: '95%+ Answer Rate',
    criteria: 'Monthly answer rate is at least 95%; missed calls returned within 10 minutes count as answered.',
    points: 1000,
    enabled: true,
    exclusions: ['Exclude test calls and calls routed to an approved coverage partner.'],
  },
  {
    id: 'answer-rate-90',
    category: 'Response Time',
    label: '90%+ Answer Rate',
    criteria: 'Monthly answer rate is at least 90%.',
    points: 500,
    enabled: true,
    exclusions: [],
  },
  {
    id: 'answer-rate-80',
    category: 'Response Time',
    label: '80%+ Answer Rate',
    criteria: 'Monthly answer rate is at least 80%.',
    points: 250,
    enabled: true,
    exclusions: [],
  },
  {
    id: 'answer-rate-below-80',
    category: 'Response Time',
    label: 'Below 80% Answer Rate',
    criteria: 'Monthly answer rate falls below 80%.',
    points: 0,
    enabled: true,
    exclusions: [],
  },
  {
    id: 'answer-rate-below-70',
    category: 'Response Time',
    label: 'Below 70% Answer Rate',
    criteria: 'Monthly answer rate falls below 70%.',
    points: -1000,
    enabled: true,
    exclusions: ['Exclude outages documented by the phone system provider.'],
  },
  {
    id: 'text-under-5',
    category: 'Response Time',
    label: 'Text response under 5 minutes',
    criteria: 'Average text response time is under five minutes.',
    points: 500,
    enabled: true,
    exclusions: ['Exclude messages received outside assigned coverage hours.'],
  },
  {
    id: 'text-over-10',
    category: 'Response Time',
    label: 'Text response over 10 minutes',
    criteria: 'Average text response time exceeds ten minutes.',
    points: -500,
    enabled: true,
    exclusions: [],
  },
  {
    id: 'teli-under-5',
    category: 'Response Time',
    label: 'Teli AI response under 5 minutes',
    criteria: 'Lead response inside Teli AI is under five minutes before conversion to Dialpad.',
    points: 500,
    enabled: true,
    exclusions: [],
  },
  {
    id: 'teli-over-10',
    category: 'Response Time',
    label: 'Teli AI response over 10 minutes',
    criteria: 'Lead response inside Teli AI exceeds ten minutes.',
    points: 0,
    enabled: true,
    exclusions: [],
  },
  {
    id: 'per-dial',
    category: 'Dial Activity',
    label: 'Per Dial',
    criteria: 'Completed outbound dial logged in the approved calling platform.',
    points: 1,
    enabled: true,
    exclusions: ['Exclude test calls, duplicate redials within 60 seconds, and wrong numbers.'],
  },
  {
    id: 'safe-practice',
    category: 'Loan Partner Requirements',
    label: 'Daily SAFE Practice Test',
    criteria: 'Daily SAFE practice test is submitted to HR.',
    points: 25,
    enabled: true,
    exclusions: [],
  },
  {
    id: 'safe-exam',
    category: 'Loan Partner Requirements',
    label: 'SAFE Exam Passed',
    criteria: 'SAFE exam is passed on the first attempt; one-time award.',
    points: 2500,
    enabled: true,
    exclusions: ['Exclude retakes and duplicate HR submissions.'],
  },
  {
    id: 'credit-pull-qc',
    category: 'Quality Control & Penalties',
    label: 'Credit Pull QC',
    criteria: 'Three consecutive Credit Pulls without sending a Doc Out; counter resets after Doc Out.',
    points: -500,
    enabled: true,
    exclusions: ['Exclude files on documented borrower-requested hold.'],
  },
  {
    id: 'file-not-moving',
    category: 'Quality Control & Penalties',
    label: 'Files Not Moving',
    criteria: 'File remains in the same status without a stage change.',
    points: -50,
    enabled: true,
    exclusions: ['Exclude loans awaiting third-party appraisal or title requirements.'],
  },
  {
    id: 'suspended-file',
    category: 'Quality Control & Penalties',
    label: 'Suspended File',
    criteria: 'File is suspended by Underwriting.',
    points: -500,
    enabled: true,
    exclusions: ['Exclude lender-initiated system holds.'],
  },
  {
    id: 'file-withdrawn',
    category: 'Quality Control & Penalties',
    label: 'File Withdrawn',
    criteria: 'Loan file is withdrawn after Credit Pull.',
    points: -500,
    enabled: true,
    exclusions: ['Exclude loans withdrawn because of a documented lender error.'],
  },
]

const SOURCE_OBJECT_BY_METRIC: Record<string, SourceObjectId> = {
  'doc-out': 'opportunity',
  'doc-out-signing': 'opportunity',
  'uwm-low-conditions': 'opportunity',
  'uwm-high-conditions': 'opportunity',
  'other-low-conditions': 'opportunity',
  'other-high-conditions': 'opportunity',
  'answer-rate-95': 'task',
  'answer-rate-90': 'task',
  'answer-rate-80': 'task',
  'answer-rate-below-80': 'task',
  'answer-rate-below-70': 'task',
  'text-under-5': 'task',
  'text-over-10': 'task',
  'teli-under-5': 'task',
  'teli-over-10': 'task',
  'per-dial': 'task',
  'safe-practice': 'task',
  'safe-exam': 'task',
}

const LEGACY_SOURCE_OBJECT_MIGRATIONS: Record<string, SourceObjectId> = {
  loan: 'opportunity',
  call: 'task',
  disclosure: 'opportunity',
  submission: 'opportunity',
  partner_activity: 'task',
}

const DEFAULT_CONDITIONS_BY_METRIC: Record<string, MetricFilterRule[]> = {
  'credit-pull': [
    {
      id: 'credit-pull-stage',
      scope: 'source',
      field: 'stageName',
      operator: 'equals',
      value: 'Credit Pulled',
    },
    {
      id: 'credit-pull-duplicate',
      scope: 'source',
      field: 'stageName',
      operator: 'not_equals',
      value: 'Duplicate Pull',
    },
  ],
  'doc-out': [
    {
      id: 'doc-out-stage',
      scope: 'source',
      field: 'stageName',
      operator: 'equals',
      value: 'Disclosures Sent',
    },
    {
      id: 'doc-out-correction',
      scope: 'source',
      field: 'stageName',
      operator: 'not_equals',
      value: 'Correction Only',
    },
  ],
  submission: [
    {
      id: 'submission-stage',
      scope: 'source',
      field: 'stageName',
      operator: 'equals',
      value: 'Submitted to Lender',
    },
  ],
  closing: [
    {
      id: 'closing-stage',
      scope: 'source',
      field: 'stageName',
      operator: 'equals',
      value: 'Closed Won',
    },
  ],
  'speed-bonus': [
    {
      id: 'speed-bonus-hold',
      scope: 'source',
      field: 'stageName',
      operator: 'not_equals',
      value: 'Borrower Requested Hold',
    },
  ],
  'answer-rate-95': [
    {
      id: 'answer-rate-95-coverage',
      scope: 'related',
      relatedRecord: 'what',
      field: 'name',
      operator: 'not_equals',
      value: 'Approved Coverage',
    },
  ],
  'per-dial': [
    {
      id: 'per-dial-status',
      scope: 'source',
      field: 'status',
      operator: 'equals',
      value: 'Completed',
    },
    {
      id: 'per-dial-test-calls',
      scope: 'source',
      field: 'status',
      operator: 'not_equals',
      value: 'Test Call',
    },
  ],
  'safe-exam': [
    {
      id: 'safe-exam-status',
      scope: 'source',
      field: 'status',
      operator: 'equals',
      value: 'Passed',
    },
  ],
  'file-not-moving': [
    {
      id: 'file-not-moving-third-party',
      scope: 'related',
      relatedRecord: 'account',
      field: 'industry',
      operator: 'not_equals',
      value: 'Third-party review pending',
    },
  ],
}

function sourceObjectFor(metric: LegacyMetricDefinition): SourceObjectId {
  const storedSourceObject = metric.sourceObject as string | undefined
  if (storedSourceObject && LEGACY_SOURCE_OBJECT_MIGRATIONS[storedSourceObject]) {
    return LEGACY_SOURCE_OBJECT_MIGRATIONS[storedSourceObject]
  }
  if (storedSourceObject && isSelectableSourceObject(storedSourceObject)) {
    return storedSourceObject
  }
  return SOURCE_OBJECT_BY_METRIC[metric.id] ?? 'opportunity'
}

function timeframeFieldFor(
  metric: LegacyMetricDefinition,
  schema: ReturnType<typeof getSourceObjectSchema>,
): string {
  const dateFields = schema.sourceFields.filter(
    (field) => field.valueType === 'date',
  )
  return (
    dateFields.find((field) => field.key === metric.timeframeField)?.key ??
    dateFields[0]?.key ??
    ''
  )
}

function normalizeRules(
  rules: Array<MetricFilterRule | string> | undefined,
  metricId: string,
  prefix: string,
  schema: ReturnType<typeof getSourceObjectSchema>,
  relatedRecords: ReturnType<typeof getRelatedRecordSchemas>,
): MetricFilterRule[] {
  return (rules ?? [])
    .filter((rule): rule is MetricFilterRule => typeof rule !== 'string')
    .map((rule, index) => {
      const scope = rule.scope === 'related' ? 'related' : 'source'
      const [legacyRelatedRecord, ...legacyFieldParts] = rule.field.split('.')
      const relatedRecord =
        scope === 'related'
          ? relatedRecords.find(
              (record) =>
                record.key ===
                (rule.relatedRecord ??
                  (legacyFieldParts.length > 0
                    ? legacyRelatedRecord
                    : undefined)),
            )
          : undefined
      const sourceFields = schema.sourceFields
      const relatedFields = relatedRecord?.fields ?? []
      const fieldValue =
        scope === 'related' && !rule.relatedRecord && legacyFieldParts.length > 0
          ? legacyFieldParts.join('.')
          : rule.field
      const availableFields = scope === 'related' ? relatedFields : sourceFields

      return {
        id: rule.id || `${metricId}-${prefix}-${index + 1}`,
        scope,
        relatedRecord: relatedRecord?.key,
        field: availableFields.some((field) => field.key === fieldValue)
          ? fieldValue
          : availableFields[0]?.key ?? '',
        operator: rule.operator || 'equals',
        value: rule.value ?? '',
      }
    })
}

function normalizeMetric(metric: LegacyMetricDefinition): PointMetricDefinition {
  const sourceObject = sourceObjectFor(metric)
  const schema = getSourceObjectSchema(sourceObject)
  const relatedRecords = getRelatedRecordSchemas(schema)
  const userField =
    schema.userFields.find((field) => field.key === metric.sourceUserField)
      ?.key ?? schema.userFields[0].key

  // Legacy configs stored two lists. Exclusions were negative conditions, so
  // they fold into the single WHERE list with their operator inverted.
  const legacyExclusions = normalizeRules(
    metric.exclusions,
    metric.id,
    'condition',
    schema,
    relatedRecords,
  ).map((rule) => ({ ...rule, operator: negateOperator(rule.operator) }))

  const storedConditions = [
    ...normalizeRules(
      metric.conditions,
      metric.id,
      'condition',
      schema,
      relatedRecords,
    ),
    ...normalizeRules(
      metric.inclusions,
      metric.id,
      'condition',
      schema,
      relatedRecords,
    ),
    ...legacyExclusions,
  ]

  const hasStoredDefinition =
    metric.conditions !== undefined || metric.inclusions !== undefined

  const { inclusions: _inclusions, exclusions: _exclusions, ...rest } = metric

  return {
    ...rest,
    sourceObject,
    sourceUserField: userField,
    timeframeField: timeframeFieldFor(metric, schema),
    conditions:
      hasStoredDefinition || storedConditions.length > 0
        ? storedConditions
        : normalizeRules(
            DEFAULT_CONDITIONS_BY_METRIC[metric.id],
            metric.id,
            'condition',
            schema,
            relatedRecords,
          ),
    customQueryEnabled: metric.customQueryEnabled ?? false,
    customQueryText: metric.customQueryText ?? '',
  }
}

export const DEFAULT_POINT_METRICS: PointMetricDefinition[] = RAW_POINT_METRICS.map(
  normalizeMetric,
)

function cloneMetrics(
  metrics: PointMetricDefinition[],
): PointMetricDefinition[] {
  return metrics.map((metric) => ({
    ...metric,
    conditions: metric.conditions.map((rule) => ({ ...rule })),
  }))
}

export function readPointMetricConfig(): PointMetricDefinition[] {
  if (typeof window === 'undefined') return cloneMetrics(DEFAULT_POINT_METRICS)

  try {
    const saved = window.localStorage.getItem(POINT_SYSTEM_CONFIG_STORAGE_KEY)
    if (!saved) return cloneMetrics(DEFAULT_POINT_METRICS)
    const parsed = JSON.parse(saved) as LegacyMetricDefinition[]
    return Array.isArray(parsed) && parsed.length > 0
      ? parsed.map(normalizeMetric)
      : cloneMetrics(DEFAULT_POINT_METRICS)
  } catch {
    return cloneMetrics(DEFAULT_POINT_METRICS)
  }
}

export function savePointMetricConfig(metrics: PointMetricDefinition[]): void {
  if (typeof window === 'undefined') return
  window.localStorage.setItem(POINT_SYSTEM_CONFIG_STORAGE_KEY, JSON.stringify(metrics))
}
